import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const MarketOverviewContext = createContext();

export const useMarketOverviewData = () => useContext(MarketOverviewContext);

export const isStatusRow = (row) => Boolean(
    row && (
        row.status_short_term !== undefined ||
        row.score_short_term !== undefined ||
        row.status_middle_term !== undefined ||
        row.score_middle_term !== undefined ||
        row.status_long_term !== undefined ||
        row.score_long_term !== undefined
    )
);

export const MarketOverviewProvider = ({ children }) => {
    const [rows, setRows] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    const normalizeItem = (item) => {
        const cat = item.category || item.Category || "";
        const catalyst = item.catalyst || item.Catalyst || "";
        const headline = item.headline || item.Headline || "";
        const date = item.date || item.Date || item.updated_at || "";
        const interpretation = item.interpretation || item.Interpretation || "";
        const impact = item.impact || item.Impact || "Medium";
        const direction = item.direction || item.Direction || "Neutral";
        const horizon = item.horizon || item.Horizon || item.duration || item.Duration || "";
        const assets = item.assets || item.Assets || catalyst;
        const sources = item.sources || item.Sources || "";

        return {
            ...item,
            category: cat,
            catalyst: catalyst,
            headline: headline,
            date: date,
            interpretation: interpretation,
            impact: impact,
            direction: direction,
            horizon: horizon,
            duration: horizon,
            assets: assets,
            sources: sources,
            Category: cat,
            Catalyst: catalyst,
            Headline: headline,
            Date: date,
            Interpretation: interpretation,
            Impact: impact,
            Direction: direction,
            Horizon: horizon,
            Duration: horizon,
            Assets: assets,
            Sources: sources,
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [statusRes, eventsRes] = await Promise.all([
                    axios.get(`${API_URL}/market-overview/status`),
                    axios.get(`${API_URL}/market-overview/events?limit=250`)
                ]);
                const statusItems = (statusRes.data || []).map(item => normalizeItem(item));
                const eventItems = (eventsRes.data || []).map(item => normalizeItem(item));
                console.log(`[Trace] Fetched ${statusItems.length} Status items and ${eventItems.length} Event items from API.`);
                setRows([...statusItems, ...eventItems]);
            } catch (error) {
                console.error("Error fetching market overview data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const refreshStatuses = async () => {
        try {
            const statusRes = await axios.get(`${API_URL}/market-overview/status`);
            const statusItems = (statusRes.data || []).map(item => normalizeItem(item));
            setRows(prev => {
                const eventItems = prev.filter(r => !isStatusRow(r));
                return [...statusItems, ...eventItems];
            });
        } catch (e) {
            console.error("Error refreshing statuses:", e);
        }
    };

    const updateRow = async (index, updatedRow) => {
        const rowToUpdate = rows[index];
        if (!rowToUpdate.id) {
            console.error("Cannot update row without ID");
            return;
        }

        try {
            // Optimistic update
            const newRows = [...rows];
            newRows[index] = updatedRow;
            setRows(newRows);

            const isStatus = isStatusRow(rowToUpdate);
            const endpoint = isStatus
                ? `${API_URL}/market-overview/status/${rowToUpdate.id}`
                : `${API_URL}/market-overview/events/${rowToUpdate.id}`;

            const payload = isStatus ? {
                category: updatedRow.Category,
                catalyst: updatedRow.Catalyst,
                headline: updatedRow.Headline,
                interpretation: updatedRow.Interpretation,
                status_short_term: updatedRow.status_short_term,
                score_short_term: updatedRow.score_short_term,
                status_middle_term: updatedRow.status_middle_term,
                score_middle_term: updatedRow.score_middle_term,
                status_long_term: updatedRow.status_long_term,
                score_long_term: updatedRow.score_long_term
            } : {
                category: updatedRow.Category,
                catalyst: updatedRow.Catalyst,
                headline: updatedRow.Headline,
                date: updatedRow.Date,
                interpretation: updatedRow.Interpretation,
                impact: updatedRow.Impact,
                direction: updatedRow.Direction,
                horizon: updatedRow.Horizon || updatedRow.Duration,
                duration: updatedRow.Horizon || updatedRow.Duration,
                assets: updatedRow.Assets,
                sources: updatedRow.Sources
            };

            await axios.put(endpoint, payload);
            if (!isStatus) {
                await refreshStatuses();
            }
        } catch (error) {
            console.error("Error updating market overview item:", error);
            window.location.reload();
        }
    };

    const deleteRow = async (index) => {
        const rowToDelete = rows[index];
        if (!rowToDelete.id) {
            console.error("Cannot delete row without ID");
            return;
        }

        try {
            setRows(prev => prev.filter((_, i) => i !== index));
            const isStatus = isStatusRow(rowToDelete);
            const endpoint = isStatus
                ? `${API_URL}/market-overview/status/${rowToDelete.id}`
                : `${API_URL}/market-overview/events/${rowToDelete.id}`;
            await axios.delete(endpoint);
            if (!isStatus) {
                await refreshStatuses();
            }
        } catch (error) {
            console.error("Error deleting market overview item:", error);
            window.location.reload();
        }
    };

    const deleteRows = async (indices) => {
        const rowsToDelete = indices.map(index => rows[index]).filter(r => r && r.id);
        const ids = rowsToDelete.map(r => r.id);

        if (ids.length === 0) return;

        try {
            // Optimistic update
            const newRows = rows.filter((_, i) => !indices.includes(i));
            setRows(newRows);

            await axios.delete(`${API_URL}/market-overview/events/bulk`, { data: ids });
            await refreshStatuses();
        } catch (error) {
            console.error("Error deleting market overview items in bulk:", error);
            window.location.reload();
        }
    };

    const addRow = async (initialData = {}) => {
        const isStatus = Boolean(
            initialData.type === 'STATUS' ||
            initialData.isStatus ||
            isStatusRow(initialData)
        );

        const newRowData = isStatus ? {
            category: initialData.Category || "Indices",
            catalyst: initialData.Catalyst || "New Asset",
            headline: initialData.Headline || "New Overview",
            interpretation: initialData.Interpretation || "",
            status_short_term: initialData.status_short_term || "Neutral",
            score_short_term: initialData.score_short_term || 0,
            status_middle_term: initialData.status_middle_term || "Neutral",
            score_middle_term: initialData.score_middle_term || 0,
            status_long_term: initialData.status_long_term || "Neutral",
            score_long_term: initialData.score_long_term || 0
        } : {
            category: initialData.Category || "Indices",
            catalyst: initialData.Catalyst || "S&P500",
            headline: initialData.Headline || "New Headline",
            date: initialData.Date || new Date().toISOString().split('T')[0],
            interpretation: initialData.Interpretation || "New Interpretation",
            impact: initialData.Impact || "Medium",
            direction: initialData.Direction || "Neutral",
            horizon: initialData.Horizon || initialData.Duration || "Short Term",
            duration: initialData.Horizon || initialData.Duration || "Short Term",
            assets: initialData.Assets || "S&P500",
            sources: initialData.Sources || "Manual"
        };

        try {
            const endpoint = isStatus ? `${API_URL}/market-overview/status` : `${API_URL}/market-overview/events`;
            const response = await axios.post(endpoint, newRowData);
            if (response.data && response.data.id) {
                const newRow = {
                    id: response.data.id,
                    Category: newRowData.category,
                    Catalyst: newRowData.catalyst,
                    Headline: newRowData.headline,
                    Date: newRowData.date || new Date().toISOString().split('T')[0],
                    Interpretation: newRowData.interpretation,
                    Impact: newRowData.impact,
                    Direction: newRowData.direction,
                    Horizon: newRowData.horizon || newRowData.duration,
                    Duration: newRowData.horizon || newRowData.duration,
                    Assets: newRowData.assets,
                    Sources: newRowData.sources,
                    status_short_term: newRowData.status_short_term,
                    score_short_term: newRowData.score_short_term,
                    status_middle_term: newRowData.status_middle_term,
                    score_middle_term: newRowData.score_middle_term,
                    status_long_term: newRowData.status_long_term,
                    score_long_term: newRowData.score_long_term
                };
                setRows(prev => [...prev, newRow]);
                if (!isStatus) {
                    await refreshStatuses();
                }
            }
        } catch (error) {
            console.error("Error creating market overview item:", error);
        }
    };

    const resetData = async () => {
        setIsLoading(true);
        try {
            // Artificial delay to show animation (UX feedback)
            await new Promise(resolve => setTimeout(resolve, 400));
            const [statusRes, eventsRes] = await Promise.all([
                axios.get(`${API_URL}/market-overview/status`),
                axios.get(`${API_URL}/market-overview/events?limit=250`)
            ]);
            const statusItems = (statusRes.data || []).map(item => normalizeItem(item));
            const eventItems = (eventsRes.data || []).map(item => normalizeItem(item));
            setRows([...statusItems, ...eventItems]);
        } catch (error) {
            console.error("Error resetting data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper to find the original index since tabs filter the data
    const getOriginalIndex = (row) => {
        return rows.findIndex(r => String(r.id) === String(row.id));
    };

    const setAllData = (newRows) => {
        setRows(newRows);
    };

    const reorderRows = (startIndex, endIndex) => {
        setRows(prev => {
            const result = Array.from(prev);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        });
    };

    return (
        <MarketOverviewContext.Provider value={{ rows, updateRow, deleteRow, deleteRows, addRow, resetData, getOriginalIndex, setAllData, reorderRows, isLoading }}>
            {children}
        </MarketOverviewContext.Provider>
    );
};
